<template>
  <div class="audience-rule">
    <!-- Header -->
    <div
      class="header"
      :class="{ negated: isNegated }">
      <div class="flex align-items-center">
        <div class="audience-node-drag flex pl-1 pr-3 pb-2 pt-3">
          <span
            class="icon"
            style="width: 10px"
            ><icon-drag
          /></span>
        </div>
        <span
          v-if="isNegated"
          class="badge badge-danger"
          >NOT</span
        >
        <b>{{ audienceRule.name }}</b>
        <div
          v-if="isEditable"
          class="actions">
          <div class="toggle danger">
            <toggle-button
              :value="isNegated"
              :sync="true"
              @change="toggleNot()" />
            <span>{{ capitalize(i18n.t('actions.avoid')) }}</span>
          </div>
          <button
            v-if="!isEditing"
            v-tooltip="capitalize(i18n.t('actions.edit'))"
            class="btn btn-xs btn-info-hollow btn-no-text"
            name="edit_rule"
            @click="editRule">
            <icon-edit />
          </button>
          <button
            v-if="parent.nodes.length > 0 || parent.parent !== null"
            v-tooltip="capitalize(i18n.t('actions.delete'))"
            class="btn btn-xs btn-danger btn-no-text"
            name="delete"
            @click="deleteRule">
            <icon-delete />
          </button>
        </div>
      </div>
      <span class="text-muted"> {{ titlecase(description) }}</span>
      <div class="description">
        <audience-rule-stats :rule="audienceRule" />
        <div class="action-collapse">
          <span
            v-show="!isEditable && !isSegmentMode"
            v-tooltip="capitalize(i18n.t(isDetailsExpanded ? 'actions.collapse' : 'actions.expand'))"
            data-testid="rule_expand_toggle"
            @click="isDetailsExpanded = !isDetailsExpanded">
            <icon-collapse v-if="isDetailsExpanded" />
            <icon-expand v-else />
          </span>
        </div>
      </div>
    </div>
    <div class="details">
      <transition-group name="squish">
        <template v-if="isEditable && isEditing">
          <div>
            <!-- GEOFENCE -->
            <geofence-rule
              v-if="mode === AudienceRuleModes.Geofence || mode === AudienceRuleModes.MastercardGeofence"
              :audience-rule="audienceRule"
              :geofence-groups="geofenceGroups"
              :validator="validator"
              :edit-mode="inlinePopups ? 'inline' : 'modal'"
              @edit-geofence-group="isEditingGeofenceGroup = true"
              @save-geofence-group="isEditingGeofenceGroup = false" />

            <!-- EXTERNAL DEMOGRAPHIC -->
            <external-demographic-rule
              v-if="mode === AudienceRuleModes.Demographic && selectedDataProvider && !isAudienceDeviceGroup"
              :audience-rule="audienceRule"
              :selected-data-provider="selectedDataProvider"
              :external-demographic-category="selectedExternalDemographicCategoryInternal"
              :validator="validator"
              @external-demographic-category-changed="(val) => (selectedExternalDemographicCategoryInternal = val)" />

            <!-- DEVICE ID SEGMENT GROUP -->
            <device-i-d-rule
              v-if="mode === AudienceRuleModes.Demographic && isAudienceDeviceGroup"
              :audience-rule="audienceRule"
              :device-i-d-group="audienceRuleCustomGroup"
              :name="name"
              :s3-path="s3Path"
              :validator="validator"
              @name-changed="(val) => (name = val)"
              @custom-group-changed="audienceRuleCustomGroupChanged" />

            <!-- EXTERNAL SEGMENT RULE (LIFESIGHT, ADSQUARE, EPSILON etc.) -->
            <external-segment-rule
              v-if="isSegmentMode"
              :mode="mode"
              :audience-rule="audienceRule"
              @on-external-segment-selected="onExternalSegmentSelected" />

            <!-- LIFESIGHT DEMOGRAPHICS RULES-->
            <lifesight-demographics-rule
              v-if="mode === AudienceRuleModes.PredefinedDemographic"
              :audience-rule="audienceRule" />

            <button
              v-show="!isEditingGeofenceGroup"
              class="btn ml-auto"
              :class="isSegmentMode ? 'btn-default' : 'btn-primary'"
              name="ok"
              :disabled="!isRuleComplete"
              @click="isEditing = false">
              {{ isSegmentMode ? i18n.t('actions.cancel') : i18n.t('actions.ok') }}
            </button>
          </div>
        </template>
      </transition-group>
      <transition name="squish">
        <audience-rule-display
          v-if="isDetailsExpanded"
          :rule="audienceRule" />
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, onBeforeMount, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useStore } from 'vuex';

  import ToggleButton from '@/common/components/controls/ToggleButton.vue';
  import { AudienceRuleModes, isExternalSegmentRule } from '@/common/enums/audience-rule-modes';
  import { DataMarketplaceCodes } from '@/common/enums/data-marketplace-codes.enum';
  import { DataProviderCodes } from '@/common/enums/data-provider-codes';
  import { IconCollapse, IconDelete, IconDrag, IconEdit, IconExpand } from '@/common/icons/monochrome';
  import { AudienceLogic, GeofenceGroup } from '@/common/models';
  import AudienceGroup from '@/common/models/audience/AudienceGroup';
  import AudienceRule from '@/common/models/audience/AudienceRule';
  import AudienceRuleCustomGroup from '@/common/models/audience/AudienceRuleCustomGroup';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { getAppInstance, titlecase, useInjected } from '@/globals';
  import LifesightDemographicsRule from '@/org-app/components/audienceLogics/LifesightDemographicsRule.vue';
  import AudienceRuleDisplay from '@/org-app/components/audienceRules/AudienceRuleDisplay.vue';
  import AudienceRuleStats from '@/org-app/components/audienceRules/AudienceRuleStats.vue';
  import DeviceIDRule from '@/org-app/components/audienceRules/rules/DeviceIDRule.vue';
  import ExternalDemographicRule from '@/org-app/components/audienceRules/rules/ExternalDemographicRule.vue';
  import ExternalSegmentRule from '@/org-app/components/audienceRules/rules/externalSegment/ExternalSegmentRule.vue';
  import { useDataProviders } from '@/org-app/services/tanstack/queries';
  import { useDataMarketplaces } from '@/org-app/services/tanstack/queries/dataMarketplaces.queries';
  import AudienceSegmentUtils from '@/utils/AudienceSegmentUtils';
  import { mapActions, mapOrgAppActions } from '@/utils/StateMapping';
  import Validator from '@/validator/Validator';

  import GeofenceRule from './rules/GeofenceRule.vue';

  // Setup
  const i18n = useI18n();
  const store = useStore();
  const props = withDefaults(
    defineProps<{
      audienceRule: AudienceRule;
      parent: AudienceGroup;
      isEditable: boolean;
      isAudienceDeviceGroup: boolean;
      geofenceGroups: GeofenceGroup[];
      inlinePopups: boolean;
    }>(),
    {
      audienceRule: null,
      isEditable: true,
      isAudienceDeviceGroup: false,
      geofenceGroups: () => [],
      inlinePopups: false,
    },
  );
  const emits = defineEmits<{
    (e: 'negationChanged', val: AudienceRule): void;
  }>();
  const { items: dataMarketplaces } = useDataMarketplaces();
  const { items: dataProviders } = useDataProviders();
  const audienceLogic = useInjected<AudienceLogic>('audienceLogic');

  // Map Actions
  const { updateAudienceRuleCustomGroupS3Path, updateAudienceRuleName, updateNodes } = mapActions();
  const { createAudienceRuleCustomGroup, updateAudienceRuleCustomGroup, getAudienceRuleCustomGroup } =
    mapOrgAppActions();

  // Ref
  const audienceRuleCustomGroup = ref<AudienceRuleCustomGroup>(props.audienceRule.audienceRuleCustomGroup);
  const selectedExternalDemographicCategoryInternal = ref(null);
  const validator = ref<Validator>(null);
  const isEditing = ref<boolean>(
    props.isEditable && props.audienceRule.isNew && !AudienceSegmentUtils.isAudienceRuleComplete(props.audienceRule),
  );
  const isDetailsExpanded = ref<boolean>(false);
  const isEditingGeofenceGroup = ref(false);

  // Computed
  const selectedDataProvider = computed(() =>
    dataProviders.value.find((dp) => dp.id === audienceLogic.value.dataProviderID),
  );
  const isRuleComplete = computed(() => AudienceSegmentUtils.isAudienceRuleComplete(props.audienceRule));
  const description = computed(() => {
    switch (props.audienceRule.mode) {
      case AudienceRuleModes.Demographic:
        return props.isAudienceDeviceGroup ? i18n.t('names.deviceIDRule') : i18n.t('names.externalDemographicRule');
      case AudienceRuleModes.PredefinedDemographic:
        return i18n.t('names.lifesightDemographicRule');
      case AudienceRuleModes.Segments:
        if (audienceLogic.value.dataMarketplace) {
          if (audienceLogic.value.dataMarketplace.code === DataMarketplaceCodes.LIVERAMP) {
            return i18n.t('names.liverampSegmentRule');
          }
        } else if (selectedDataProvider.value) {
          switch (selectedDataProvider.value.code) {
            case DataProviderCodes.ADSQUARE:
              return i18n.t('names.adsquareSegmentRule');
            case DataProviderCodes.EPSILON:
              return i18n.t('names.epsilonSegmentRule');
            case DataProviderCodes.MASTERCARD:
              return i18n.t('names.mastercardSegmentRule');
            case DataProviderCodes.LIFESIGHT_EU:
            case DataProviderCodes.LIFESIGHT_NA:
            default:
              return i18n.t('names.lifesightSegmentRule');
          }
        }
        return i18n.t('names.lifesightSegmentRule');
      case AudienceRuleModes.Epsilon:
        return i18n.t('names.epsilonSegmentRule');
      case AudienceRuleModes.MastercardGeofence:
        return i18n.t('names.mastercardSegmentRule');
      case AudienceRuleModes.Geofence:
      default:
        return i18n.t('names.geofenceGroupRule');
    }
  });
  const isNegated = computed(() => props.audienceRule.isNegated ?? false);
  const organization = computed(() => store.state.orgApp.organization);
  const mode = computed(() => props.audienceRule.mode);
  const isSegmentMode = computed(() => isExternalSegmentRule(mode.value));
  const name = computed({
    get() {
      return props.audienceRule.name;
    },
    set(value) {
      updateAudienceRuleName({ audienceRule: props.audienceRule, value });
    },
  });
  const s3Path = computed({
    get() {
      return audienceRuleCustomGroup.value != null ? audienceRuleCustomGroup.value.s3Path : '';
    },
    set(value) {
      updateAudienceRuleCustomGroupS3Path({
        audienceRuleCustomGroup: audienceRuleCustomGroup.value,
        value,
      });
    },
  });

  // Expose
  defineExpose({
    save,
    update,
    deleteRule,
    parent: props.parent,
    isNegated: props.audienceRule.isNegated,
    isEditing,
    audienceRule: props.audienceRule,
    audienceRuleCustomGroup,
    $validator: validator,
  });

  // Methods
  async function save() {
    const newAudienceRuleCustomGroup = new AudienceRuleCustomGroup(
      props.audienceRule.name,
      audienceRuleCustomGroup.value.s3Path,
      organization.value.id,
      audienceRuleCustomGroup.value.externalSegmentID ?? -1,
    );
    audienceRuleCustomGroup.value = newAudienceRuleCustomGroup;
    const createdAudienceRuleCustomGroup: AudienceRuleCustomGroup = await createAudienceRuleCustomGroup({
      instances: [newAudienceRuleCustomGroup],
    });
    newAudienceRuleCustomGroup.customGroupID = createdAudienceRuleCustomGroup.customGroupID;
    audienceRuleCustomGroup.value = createdAudienceRuleCustomGroup;
    return createdAudienceRuleCustomGroup;
  }
  async function update() {
    const { customGroupID, name } = props.audienceRule;
    const newAudienceRuleCustomGroup = new AudienceRuleCustomGroup(
      name,
      audienceRuleCustomGroup.value.s3Path,
      organization.value.id,
      audienceRuleCustomGroup.value.externalSegmentID ?? -1,
      customGroupID,
    );
    await updateAudienceRuleCustomGroup({
      data: newAudienceRuleCustomGroup.toApi(),
      params: { id: newAudienceRuleCustomGroup.customGroupID },
    });
  }
  function editRule() {
    isEditing.value = true;
    GlobalEventBus.$emit(GlobalEvents.clearTooltips);
  }
  function deleteRule() {
    if (props.parent !== null) {
      const nodes = [...props.parent.nodes.filter((x) => x !== props.audienceRule)];
      updateNodes({ entity: props.parent, value: nodes });
    }
    selectedExternalDemographicCategoryInternal.value = null;
    GlobalEventBus.$emit(GlobalEvents.deleteAudienceRule, props.audienceRule);
    GlobalEventBus.$emit(GlobalEvents.clearTooltips);
  }
  function toggleNot() {
    emits('negationChanged', props.audienceRule);
    GlobalEventBus.$emit(GlobalEvents.audienceLogicExpressionChanged);
  }
  function onExternalSegmentSelected(newAudienceRuleCustomGroup: AudienceRuleCustomGroup) {
    audienceRuleCustomGroupChanged(newAudienceRuleCustomGroup);
    isEditing.value = false;
  }

  function audienceRuleCustomGroupChanged(newAudienceRuleCustomGroup: AudienceRuleCustomGroup) {
    audienceRuleCustomGroup.value = newAudienceRuleCustomGroup;
    s3Path.value = newAudienceRuleCustomGroup.s3Path;
    store.dispatch('updateAudienceRuleCustomGroup', {
      audienceRule: props.audienceRule,
      value: newAudienceRuleCustomGroup,
    });
  }

  // Lifecycle
  onBeforeMount(() => {
    validator.value = new Validator(getAppInstance());
    validator.value.init(props.audienceRule);
  });
  onMounted(async () => {
    if (props.audienceRule != null && props.audienceRule.customGroupID != null) {
      audienceRuleCustomGroup.value = await getAudienceRuleCustomGroup({
        id: props.audienceRule.customGroupID,
      });
      s3Path.value = audienceRuleCustomGroup.value.s3Path;
    } else {
      audienceRuleCustomGroup.value = new AudienceRuleCustomGroup();
    }

    if (props.audienceRule.audienceRuleCustomGroup) {
      s3Path.value = props.audienceRule.audienceRuleCustomGroup.s3Path;
    }
  });
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .audience-node-drag {
    cursor: grab;
  }

  #app {
    .audience-rule {
      border: solid 0.1rem $borders;
      border-radius: 0.4rem;
      background-color: #fff;

      &.dragover {
        transform: scale(1.05);
        background: var(--secondaryColorFaded95);
        border: solid 0.1rem $borders;
        padding: 1.6rem;
      }

      .header {
        background: var(--paneHeaderBackgroundColor);

        padding: 1.6rem;
        border-radius: 0.4rem 0.4rem 0 0;

        display: flex;
        flex-direction: column;
        align-items: flex-start;
        position: relative;
        > div {
          width: 100%;
        }

        .icon-warn {
          display: flex;
          align-items: center; /* Center vertically */
          margin-left: 0.5rem;

          svg {
            stroke: var(--warningDark);
            fill: var(--warningDark);
            height: 1.6rem;
            margin-left: 0.5rem;
          }
        }

        .badge {
          margin-right: 0.6rem;
        }

        &.negated {
        }

        .actions {
          margin-left: auto;
          float: right;
          display: flex;
          gap: 1rem;

          .toggle {
            margin-top: 0;
            margin-bottom: 0;
            > section {
              margin-right: -1rem;
            }
          }
        }
      }

      .description {
        display: grid;
        grid-template-columns: 50% 50%;

        .action-collapse {
          width: 2rem;
          height: 2rem;
          align-self: end;
          justify-self: end;

          > span {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 2rem;
            height: 2rem;
          }

          svg {
            stroke: var(--primaryColor);
          }
        }
      }

      .details {
        & > div {
          margin: 1.6rem;
        }
        background: #fff;
        border-radius: 0 0 0.4rem 0.4rem;
      }
    }
  }
</style>
